import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Campaign, 
  Organization, 
  OrgInstance 
} from "@dndModels";

import API from "@dndUtilities/system_api";

import ButtonBox from "@components/input/buttons/ButtonBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBox";
import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props { 
  campaign: Campaign;
  onChange: (changed: Campaign) => void;
}

export default function CampaignOrgInstancesInput({ 
  campaign, onChange
}: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [addingMethod, setAddingMethod] = useState("");
  const [usingOrgInstance, setUsingOrgInstance] = useState<OrgInstance | null>();
  const [myOrgInstances, setMyOrgInstances] = useState<OrgInstance[]>([]);
  const [orgInstances, setOrgInstances] = useState<OrgInstance[]>([]);
  // const [myValues, setValues] = useState(values);
  // const [newSuggestionMode, setNewSuggestionMode] = useState(false);
  // const [newSuggestionStr, setNewSuggestionStr] = useState("");
  
  const api = API.getInstance();

  // passing an empty array as second argument triggers the callback in useEffect
  // only after the initial render thus replicating `componentDidMount` lifecycle behaviour
  useEffect(() => {
    if(!fetched) {
      load();
    }
  });

  // useEffect(() => {
  //   setValue(value);
  // }, [value]);

  // useEffect(() => {
  //   setValues(values);
  // }, [values]);

  const load = () => {
    setFetched(true);
    setLoading(true);
    api.getObjects({data_type: "org_instance", filter: { owner_id: api.user ? api.user._id : "" }}).then(res => {
      const org_instances = res as OrgInstance[];
      setMyOrgInstances(org_instances);
      setOrgInstances(org_instances.filter(o => o.campaign_ids.includes(campaign._id)));
      setLoading(false);
    });
  }

  const addNew = () => {
    setUsingOrgInstance(new OrgInstance());
    setAddingMethod(addingMethod === "New" ? "" : "New");
  };

  const addExisting = () => {
    setUsingOrgInstance(null);
    setAddingMethod(addingMethod === "Existing" ? "" : "Existing");
  };

  const selectOrgInstance = (org_instance: OrgInstance) => {
    setUsingOrgInstance(org_instance);
  };

  const addInstance = () => {
    if (usingOrgInstance) {
      usingOrgInstance.campaign_ids.push(campaign._id);
      setProcessing(true);
      if (addingMethod === "New") {
        usingOrgInstance.owner_id = api.user ? api.user._id : "";
        api.upsertObject("org_instance", usingOrgInstance).then(res => {
          if (res) {
            const newUsingOrgInstance = usingOrgInstance;
            newUsingOrgInstance._id = res.body.id;
            campaign.org_inst_ids.push(newUsingOrgInstance._id);

            const newOrgInstances = orgInstances;
            newOrgInstances.push(newUsingOrgInstance);
            setOrgInstances(newOrgInstances);
            const newMyOrgInstances = myOrgInstances;
            newMyOrgInstances.push(newUsingOrgInstance);
            setMyOrgInstances(newMyOrgInstances);

            onChange(campaign);
          }
          setAddingMethod("");
          setProcessing(false);
        });
      } else {
        campaign.org_inst_ids.push(usingOrgInstance._id);
        api.upsertObject("org_instance", usingOrgInstance).then(res => {
          // const newUsingOrgInstance = usingOrgInstance;
          // newUsingOrgInstance._id = res.body.id;
          // campaign.org_inst_ids.push(newUsingOrgInstance._id);

          // const newOrgInstances = orgInstances;
          // newOrgInstances.push(newUsingOrgInstance);
          // setOrgInstances(newOrgInstances);
          // const newMyOrgInstances = myOrgInstances;
          // newMyOrgInstances.push(newUsingOrgInstance);
          // setMyOrgInstances(newMyOrgInstances);

          // onChange(campaign);
            // setAddingMethod("");
          const newOrgInstances = orgInstances;
          newOrgInstances.push(usingOrgInstance);
          setOrgInstances(newOrgInstances);
          onChange(campaign);
          setUsingOrgInstance(null);
          setProcessing(false);
        });
      }
    }
  };

  const updateInstance = () => {
    if (usingOrgInstance) {
      setProcessing(true);
      api.upsertObject("org_instance", usingOrgInstance).then(res => {
        onChange(campaign);
        setUsingOrgInstance(null);
        setProcessing(false);
      });
    }
  };

  const removeInstance = () => {
    if (usingOrgInstance) {
      usingOrgInstance.campaign_ids = usingOrgInstance.campaign_ids.filter(o => o !== campaign._id);
      setProcessing(true);
      api.upsertObject("org_instance", usingOrgInstance).then(res => {
        campaign.org_inst_ids = campaign.org_inst_ids.filter(o => o !== usingOrgInstance._id);

        const newOrgInstances = orgInstances.filter(o => o._id !== usingOrgInstance._id)
        setOrgInstances(newOrgInstances);

        onChange(campaign);
        setAddingMethod("");
        setProcessing(false);
      });
    }
  };

  const renderAddingMethod = () => {
    let return_me: any = null;
    const owner_id = api.user ? api.user._id : "";
    switch(addingMethod) {
      case "New": 
        if (usingOrgInstance) {
          return_me = (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <StringBox 
                  name="Name"
                  value={usingOrgInstance.name} 
                  message={usingOrgInstance.name.length > 0 ? "" : "Name Invalid"}
                  disabled={processing}
                  onBlur={(value: string) => {
                    const newUsingOrgInstance = usingOrgInstance;
                    newUsingOrgInstance.name = value;
                    setUsingOrgInstance(null);
                    setUsingOrgInstance(newUsingOrgInstance);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <StringBox 
                  name="Description"
                  value={usingOrgInstance.description} 
                  multiline
                  disabled={processing}
                  onBlur={(value: string) => {
                    const newUsingOrgInstance = usingOrgInstance;
                    newUsingOrgInstance.description = value;
                    setUsingOrgInstance(null);
                    setUsingOrgInstance(newUsingOrgInstance);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <SelectObjectBox 
                  name="Organization"
                  data_type="organization"
                  value={usingOrgInstance.org_id}
                  disabled={processing}
                  onChangeObject={(org: Organization) => {
                    const newUsingOrgInstance = usingOrgInstance.clone();
                    newUsingOrgInstance.org_id = org._id;
                    if (newUsingOrgInstance.name === "") {
                      newUsingOrgInstance.name = `${org.name} - ${campaign.name}`;
                    }
                    setUsingOrgInstance(newUsingOrgInstance);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <StringBox
                  name="Level"
                  value={usingOrgInstance.level}
                  type="number"
                  disabled={processing}
                  onBlur={(value: string) => {
                    const newUsingOrgInstance = usingOrgInstance;
                    newUsingOrgInstance.level = +value;
                    setUsingOrgInstance(null);
                    setUsingOrgInstance(newUsingOrgInstance);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <ButtonBox
                  name="Add"
                  disabled={ processing || usingOrgInstance.org_id === "" || usingOrgInstance.name === "" }
                  onClick={addInstance}
                />
              </Grid>
            </Grid>
          );
        }
      break;
      case "Existing": 
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 9
              }}>
              <SelectObjectBox
                name="Instance"
                data_type="org_instance"
                filter={{ owner_id }}
                value={ usingOrgInstance ? usingOrgInstance._id : "" }
                onChangeObject={selectOrgInstance}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <ButtonBox
                name="Add"
                disabled={ usingOrgInstance ? usingOrgInstance.campaign_ids.includes(campaign._id) : true }
                onClick={addInstance}
              />
            </Grid>
          </Grid>
        );
      break;
    }
    return return_me;
  }

  const renderOrgInstances = () => {
    if (addingMethod === "" && !usingOrgInstance) {
      // Controls to edit or remove org instances from this campaign
      return orgInstances.map((org_inst, key) => {
        return (
          <Grid
            key={key}
            container
            spacing={0}
            direction="row"
            style={{ backgroundColor: (key % 2 === 1 ? "lightblue" : "inherit") }}>
            <Grid
              onClick={() => {
                setUsingOrgInstance(org_inst);
              }}
              style={{ cursor: "pointer" }}
              size={{
                xs: 9
              }}>
              { org_inst.name }
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <ButtonBox 
                name="Remove"
                onClick={() => {
                  removeInstance();
                }}
              />
            </Grid>
          </Grid>
        );
      });
    }
    return null;
  };

  const renderEditingOrgInstance = () => {
    if (addingMethod === "" && usingOrgInstance) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Name"
              value={usingOrgInstance.name} 
              message={usingOrgInstance.name.length > 0 ? "" : "Name Invalid"}
              disabled={processing}
              onBlur={(value: string) => {
                const newUsingOrgInstance = usingOrgInstance;
                newUsingOrgInstance.name = value;
                setUsingOrgInstance(null);
                setUsingOrgInstance(newUsingOrgInstance);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Description"
              value={usingOrgInstance.description} 
              multiline
              disabled={processing}
              onBlur={(value: string) => {
                const newUsingOrgInstance = usingOrgInstance;
                newUsingOrgInstance.description = value;
                setUsingOrgInstance(null);
                setUsingOrgInstance(newUsingOrgInstance);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectObjectBox 
              name="Organization"
              data_type="organization"
              value={usingOrgInstance.org_id}
              disabled={processing}
              onChangeObject={(org: Organization) => {
                const newUsingOrgInstance = usingOrgInstance.clone();
                newUsingOrgInstance.org_id = org._id;
                if (newUsingOrgInstance.name === "") {
                  newUsingOrgInstance.name = `${org.name} - ${campaign.name}`;
                }
                setUsingOrgInstance(newUsingOrgInstance);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Level"
              value={usingOrgInstance.level}
              type="number"
              disabled={processing}
              onBlur={(value: string) => {
                const newUsingOrgInstance = usingOrgInstance;
                newUsingOrgInstance.level = +value;
                setUsingOrgInstance(null);
                setUsingOrgInstance(newUsingOrgInstance);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <ButtonBox
              name="Update"
              disabled={processing}
              onClick={updateInstance}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  if (!fetched || loading) {
    return (
      <LoadingBox />
    );
  } else {
    return (
      <Grid container spacing={0} direction="column">
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 6
            }}>
            <ToggleButtonBox
              name="Add New"
              value={addingMethod === "New"}
              onToggle={addNew}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <ToggleButtonBox
              name="Add Existing"
              value={addingMethod === "Existing"}
              onToggle={addExisting}
            />
          </Grid>
        </Grid>
        { renderAddingMethod() }
        { renderOrgInstances() }
        { renderEditingOrgInstance() }
      </Grid>
    );
  }
}
